import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@nextui-org/input";
import messages from "../../errorTypes/Types.ts";
import { onKeyDown } from "../../utils/onKeyDownFunction.js";
import {
  clearInputError,
  validateEachType,
  validateEachTypeQuantity,
} from "../../utils/validateEachType.js";

const Blachy = ({ density, onWeightChange }) => {
  const [plotid, setPlotid] = useState("");
  const [storis, setStoris] = useState("");
  const [ilgis, setIlgis] = useState("");
  const [count, setCount] = useState(1);

  const [errorPlotId, setErrorPlotId] = useState(null);
  const [errorStoris, setErrorStoris] = useState(null);
  const [errorIlgis, setErrorIlgis] = useState(null);
  const [errorCount, setErrorCount] = useState(null);
  const calculateWeight = useCallback(() => {
    const formattedPlotid = plotid.replace(",", ".");
    const formattedStoris = storis.replace(",", ".");
    const formattedIlgis = ilgis.replace(",", ".");
    const plotId_m = Number(formattedPlotid) / 1000; // mm -> m
    const storis_m = Number(formattedStoris) / 1000; // mm -> m
    const ilgis_m = Number(formattedIlgis) / 1000; // mm -> m
    const density_kg_m3 = density * 1000; // kg/m^3
    const volume = plotId_m * storis_m * ilgis_m; // m^3
    const weight = volume * density_kg_m3 * count;

    onWeightChange({
      totalBlacha: weight.toFixed(3),
    });
  }, [plotid, storis, ilgis, count, density, onWeightChange]);

  useEffect(() => {
    const handler = setTimeout(() => {
      calculateWeight();
    }, 100); // Opóźnienie 100ms

    return () => {
      clearTimeout(handler);
    };
  }, [calculateWeight]);

  return (
    <div className="obliczenia">
      <div className="obliczenia-inputs">
        <label htmlFor="plotid-input">
          [A] Wpisz szerokość (mm)
        </label>
        <Input
          id="plotid-input"
          type="text"
          autoComplete="off"
          value={plotid}
          classNames={{
            inputWrapper:
              "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0",
            input: "h-9",
          }}
          onChange={(e) => {
            const formattedValue = e.target.value.replace(".", ",");
            const integerPart = formattedValue.split(",")[0];
            const decimalPart = formattedValue.split(",")[1];
            if (integerPart.length > 6) {
              setErrorPlotId(messages.unicError.maxInteger);
              return;
            }
            if (decimalPart && decimalPart.length > 3) {
              setErrorPlotId(messages.unicError.maxDecimal);
              return;
            }

            if (!formattedValue.trim()) {
              setErrorPlotId(messages.unicError.empty);
              return;
            }
            if (!validateEachType(formattedValue)) {
              setErrorPlotId(messages.unicError.invalid);
              return;
            }
            setPlotid(formattedValue);
            setErrorPlotId(null);
          }}
          onBlur={() => {
            if (!validateEachType(plotid)) {
              setErrorPlotId(messages.unicError.empty);
              return;
            }
            clearInputError(plotid, setErrorPlotId);
          }}
          onKeyDown={(e) => onKeyDown(e, setPlotid)}
        />
        {errorPlotId && <p className="text-error-text">{errorPlotId}</p>}
      </div>
      <div className="obliczenia-inputs">
        <label htmlFor="storis-input">
          [B] Wpisz grubość (mm)
        </label>
        <Input
          id="storis-input"
          type="text"
          autoComplete="off"
          value={storis}
          classNames={{
            inputWrapper:
              "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0",
            input: "h-9",
          }}
          onChange={(e) => {
            const formattedValue = e.target.value.replace(".", ",");
            const integerPart = formattedValue.split(",")[0];
            const decimalPart = formattedValue.split(",")[1];
            if (integerPart.length > 6) {
              setErrorStoris(messages.unicError.maxInteger);
              return;
            }
            if (decimalPart && decimalPart.length > 3) {
              setErrorStoris(messages.unicError.maxDecimal);
              return;
            }
            if (!formattedValue.trim()) {
              setErrorStoris(messages.unicError.empty);
              return;
            }
            if (!validateEachType(formattedValue)) {
              setErrorStoris(messages.unicError.invalid);
              return;
            }
            setStoris(formattedValue);
            setErrorStoris(null);
          }}
          onBlur={() => {
            if (!validateEachType(storis)) {
              setErrorStoris(messages.unicError.empty);
              return;
            }
            clearInputError(storis, setErrorStoris);
          }}
          onKeyDown={(e) => onKeyDown(e, setStoris)}
        />
        {errorStoris && <p className="text-error-text">{errorStoris}</p>}
      </div>
      <div className="obliczenia-inputs">
        <label htmlFor="ilgis-input">
          [C] Wpisz długość (mm)
        </label>
        <Input
          id="ilgis-input"
          type="text"
          autoComplete="off"
          value={ilgis}
          classNames={{
            inputWrapper:
              "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0",
            input: "h-9",
          }}
          onChange={(e) => {
            const formattedValue = e.target.value.replace(".", ",");
            const integerPart = formattedValue.split(",")[0];
            const decimalPart = formattedValue.split(",")[1];
            if (integerPart.length > 6) {
              setErrorIlgis(messages.unicError.maxInteger);
              return;
            }
            if (decimalPart && decimalPart.length > 3) {
              setErrorIlgis(messages.unicError.maxDecimal);
              return;
            }
            if (!formattedValue.trim()) {
              setErrorIlgis(messages.unicError.empty);
              return;
            }
            if (!validateEachType(formattedValue)) {
              setErrorIlgis(messages.unicError.invalid);
              return;
            }
            setIlgis(formattedValue);
            setErrorIlgis(null);
          }}
          onBlur={() => {
            if (!validateEachType(ilgis)) {
              setErrorIlgis(messages.unicError.empty);
              return;
            }
            clearInputError(ilgis, setErrorIlgis);
          }}
          onKeyDown={(e) => onKeyDown(e, setIlgis)}
        />
        {errorIlgis && <p className="text-error-text">{errorIlgis}</p>}
      </div>
      <div className="obliczenia-inputs">
        <label htmlFor="count-input">
          Wpisz ilość (szt.)
        </label>
        <Input
          id="count-input"
          type="text"
          autoComplete="off"
          value={count}
          classNames={{
            inputWrapper:
              "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0",
            input: "h-9",
          }}
          onChange={(e) => {
            const inputValue = e.target.value.trim();
            const formattedValue =
              inputValue === null || inputValue === undefined
                ? "0"
                : inputValue;
            if (formattedValue.length > 6) {
              setErrorCount(messages.unicErrorQuantity.maxInteger);
              return;
            }
            if (!formattedValue.trim()) {
              setErrorCount(messages.unicErrorQuantity.empty);
              return;
            }
            if (!validateEachTypeQuantity(formattedValue)) {
              setErrorCount(messages.unicErrorQuantity.invalid);
              return;
            }
            setCount(formattedValue);
            setErrorCount(null);
          }}
          onBlur={() => {
            if (!validateEachTypeQuantity(count)) {
              setErrorCount(messages.unicErrorQuantity.empty);
              return;
            }
            clearInputError(count, setErrorCount);
          }}
          onKeyDown={(e) => onKeyDown(e, setCount)}
        />
        {errorCount && <p className="text-error-text">{errorCount}</p>}
      </div>
    </div>
  );
};

export default Blachy;
